import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import { Link, useNavigate, useLocation } from "react-router-dom";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import MainLogo from "../../assets/images/AstraLogo.svg";
import { verificationCode, resendVerificationCode } from "../../shared/_services/auth.services";
import { getLandingRouteForUser } from "../../shared/_helpers/landingRoute";
import { readCurrentUser } from "../../shared/_helpers/currentUser";
import { useAuth } from "../../context/AuthContext.jsx";
import {
  clearChallengeToken,
  getChallengePurpose,
  getChallengeToken,
  setChallengeToken,
  setResetToken,
} from "../../shared/_helpers/otpChallenge";

const VerifyCode = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { refetch: refetchAuth } = useAuth();
  const [timer, setTimer] = useState(30);
  const [timerActive, setTimerActive] = useState(false);
  const location = useLocation();
  const stateFrom = location?.state?.from;
  const storedFrom = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('verifyCodeFrom') : null;
  const fromPage = stateFrom || storedFrom || null;

  useEffect(() => {
    if (stateFrom) {
      try {
        sessionStorage.setItem('verifyCodeFrom', stateFrom);
        console.log('[Verification] Persisted verifyCodeFrom to sessionStorage:', stateFrom);
      } catch (_) {}
    }
  }, [stateFrom]);

  useEffect(() => {
    const path = getBackPath();
    console.log('[Verification] location.state:', location?.state, '| fromPage:', fromPage, '| Back path:', path);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- log once on mount

  const getBackPath = () => {
    if (fromPage === 'login' || fromPage === 'login-advisor' || fromPage === 'login-admin' || fromPage === 'no-package' || fromPage === 'login-canclledPlan') return '/login';
    if (fromPage === 'signup') return '/signup';
    if (fromPage === 'forgotpassword') return '/forgot-password';
    return '/login';
  };

  const initialValues = {
    otp1: "",
    otp2: "",
    otp3: "",
    otp4: "",
    otp5: "",
    otp6: "",
  };

  const validationSchema = Yup.object().shape({
    otp1: Yup.string().required(),
    otp2: Yup.string().required(),
    otp3: Yup.string().required(),
    otp4: Yup.string().required(),
    otp5: Yup.string().required(),
    otp6: Yup.string().required(),
  }).test('allFilled', 'All verification code fields are required', function (value) {
    return Object.values(value).every(otp => otp.trim() !== '');
  });

  const getOtpSlotIndex = (name) => {
    const m = /^otp(\d+)$/.exec(name);
    return m ? parseInt(m[1], 10) : NaN;
  };

  const handleInputChange = (e, setFieldValue) => {
    const { maxLength, value, name } = e.target;
    const slot = getOtpSlotIndex(name);

    if (value.length >= maxLength && !Number.isNaN(slot) && slot < 6) {
      const nextField = document.querySelector(`input[name=otp${slot + 1}]`);
      if (nextField !== null) {
        nextField.focus();
      }
    }
    setFieldValue(name, value);
  };

  const handleKeyDown = (e, setFieldValue, values) => {
    const { name, value } = e.target;
    const slot = getOtpSlotIndex(name);

    if (e.key === 'Backspace' && !value && !Number.isNaN(slot) && slot > 1) {
      const prevField = document.querySelector(`input[name=otp${slot - 1}]`);
      if (prevField !== null) {
        prevField.focus();
        setFieldValue(`otp${slot - 1}`, '');
      }
    }
  };

  const handlePaste = (e, setFieldValue, validateForm) => {
    const paste = e.clipboardData.getData('text');
    if (paste.length === 6) {
      paste.split('').forEach((char, index) => {
        setFieldValue(`otp${index + 1}`, char);
      });
      document.querySelector(`input[name=otp6]`).focus();

      setTimeout(() => {
        validateForm();
      }, 0);
    }
    e.preventDefault();
  };


  const handleSubmit = async (values) => {
    const otp = `${values.otp1}${values.otp2}${values.otp3}${values.otp4}${values.otp5}${values.otp6}`;

    // Backend SEC-114: the code is redeemable only by the request that asked
    // for it. `email` alone was the old credential and is now ignored by the
    // server — the target account is the challenge token's subject — so a
    // missing challenge is a dead end, not something to retry with an email.
    const challengeToken = getChallengeToken();
    // The forgot-password flow is the one entrance whose next screen needs a
    // credential of its own: POST /users/reset-password requires the single-use
    // reset token (backend SEC-084), and /otp-verify only mints it when the code
    // is redeemed with this purpose. No other entrance may send it — a reset
    // token minted during an ordinary login would be a live password-change
    // credential nobody asked for, and /otp-verify returns it INSTEAD of the
    // session, so that login would also silently fail.
    //
    // Read from the challenge, never from `fromPage`. The `verifyCodeFrom`
    // breadcrumb outlives its flow: Login stores the challenge before it decides
    // whether to navigate, and ClientDashboard sends an unverified user here with
    // neither navigation state nor a breadcrumb write — so a live login challenge
    // can meet a stale `forgotpassword` breadcrumb.
    const challengePurpose = getChallengePurpose();
    const isPasswordResetFlow = challengePurpose === "reset_password";

    if (!challengeToken) {
      enqueueSnackbar(
        "This verification link has expired. Please sign in again to get a new code.",
        { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" } }
      );
      navigate(getBackPath());
      return;
    }

    try {
      let data = await verificationCode({
        otp,
        challengeToken,
        ...(isPasswordResetFlow ? { purpose: "reset_password" } : {}),
      });

      if (data.status === 200) {
        // Defaulted, not passed straight through: notistack throws on an
        // undefined message, and this call sits in front of the session write —
        // a 200 that happened to carry no `message` would throw into the catch
        // below and silently lose the token /otp-verify just minted.
        enqueueSnackbar(data?.data?.message || "Verification successful.", {
          variant: "success",
          anchorOrigin: { horizontal: "right", vertical: "top" },
        });

        // Redeemed — it is single-use, so it must not survive into a later flow.
        clearChallengeToken();

        // readCurrentUser, not a raw JSON.parse: localStorage is user-editable
        // and a corrupt value would throw into the catch below, losing the
        // session token this response just carried (SEC-028's reasoning, applied
        // to a write rather than a read).
        const currentUser = readCurrentUser() || {};

        // SEC-114 moved the session here: login's response no longer carries a
        // token, and this is the first point at which both factors have been
        // presented. `data.data.data` is present only for a login challenge
        // (`pwd: true`); the signup and forgot-password entrances verify a
        // phone or mint a reset token and yield no session.
        const session = data?.data?.data;
        if (session?.token) {
          currentUser.user = session;
          currentUser.token = session.token;
          currentUser.refreshToken = session.refreshToken;
        }

        // SEC-084: the proof of the OTP step that /reset-password now demands.
        setResetToken(data?.data?.resetToken);

        if (currentUser.user) {
          currentUser.user.isPhoneVerified = true;
          localStorage.setItem("currentUser", JSON.stringify(currentUser));
          // SEC-066: AuthContext already ran its one verification pass on
          // app load, before this screen minted a session, and settled on
          // "unauthenticated". Without refetching here, the next
          // ProtectedRoutes-guarded screen would trust that stale status and
          // bounce straight back to "/" despite the token that now exists.
          if (session?.token) {
            refetchAuth();
          }
        }

        // Decided by the credential, for the same reason the request is: only a
        // challenge that actually carried the reset purpose may land the user on
        // the password-reset screen. A stale `forgotpassword` breadcrumb falls
        // through to the switch below, which no longer has a case for it.
        if (isPasswordResetFlow) {
          navigate("/reset-password", { state: { from: "verification" } });
          return;
        }

        switch (fromPage) {
          // The three post-login markers land on the same per-role route the
          // role guards redirect to — one mapping, in landingRoute.js.
          case "login":
          case "login-advisor":
          case "login-admin":
            navigate(getLandingRouteForUser(currentUser?.user));
            break;
          case "signup":
            navigate("/welcome"); //package
            break;
          case "no-package":
            navigate("/dashboard");
            break;
          case "login-canclledPlan":
            navigate("/package", { state: { from: "login-canclledPlan" } });
            break;
          default:
            navigate("/welcome"); //package
        }
      } else if (data.response?.status === 400) {
        enqueueSnackbar(data?.response?.data?.message, {
          variant: "error",
          anchorOrigin: { horizontal: "right", vertical: "top" },
        });
      } else if (data.response?.status === 401) {
        // The challenge expired (five minutes) or the account was blocked
        // between the password step and this one. Either way it cannot be
        // recovered from this screen, and a stale token must not be replayed.
        clearChallengeToken();
        enqueueSnackbar(
          data?.response?.data?.message ||
            "This verification session has expired. Please sign in again.",
          { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" } }
        );
        navigate(getBackPath());
      }
    } catch (error) {
      console.error("Error verifying verification code:", error);
    }
  };


  useEffect(() => {
    let interval = null;
    if (timerActive) {
      interval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer === 1) {
            setTimerActive(false); // Disable timer after 30 secs
            clearInterval(interval);
          }
          return prevTimer - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerActive]);

  const handleResend = async () => {
    // Backend SEC-114: a resend belongs to the flow that started it, so it
    // presents the outstanding challenge rather than a bare email — an email
    // alone no longer buys SMS sends to someone else's phone.
    const challengeToken = getChallengeToken();
    if (!challengeToken) {
      enqueueSnackbar("This verification link has expired. Please sign in again to get a new code.", {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      navigate(getBackPath());
      return;
    }

    const response = await resendVerificationCode({ challengeToken });
    if (response.status === 200) {
      // The re-issued challenge keeps the original flow anchor and replaces the
      // one just spent; the superseded code no longer verifies (INT-016), so
      // keeping the old token would strand the new code.
      setChallengeToken(response?.data?.challengeToken, {
        purpose: getChallengePurpose(),
      });
      enqueueSnackbar(`A verification code has been resent to your registered phone number and email address.`, {
        variant: 'success',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      setTimer(30); // Reset timer to 30 seconds
      setTimerActive(true); // Start timer countdown
    } else if (response?.response?.status === 401) {
      // The 15-minute flow window has passed, or the challenge expired.
      clearChallengeToken();
      enqueueSnackbar(response?.response?.data?.message || "This verification session has expired. Please sign in again.", {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      navigate(getBackPath());
    } else {
      enqueueSnackbar(response?.response?.data?.error?.message || "Failed to resend verification code", {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
    }
  };

  // Start timer initial load
  useEffect(() => {
    setTimerActive(true);
    return () => {
      setTimerActive(false);
    };
  }, []);


  return (
    <div className="login-signup-wrap forget-wrap">
      <div className="container">
        <div className="row">
          <div className="col-md-12 col-lg-6 logo-wrap place-content-center">
            <Link>
              <img className="login-signup-logo" src={MainLogo} alt="Main Logo" />
            </Link>
            <div className="auth-page-content">
              <h2>
                Verify Your Account
              </h2>
              <p>
                We’ve sent a verification code to your registered phone number & email. Please enter the code below to confirm your identity and continue to your financial dashboard.
              </p>
              <p className="w-100">
                <svg width="46" height="3" viewBox="0 0 46 3" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 1.5H46" stroke="white" strokeWidth="2" />
                </svg>
                &nbsp;&nbsp;&nbsp;
                PROFESSIONAL FINANCIAL ADVISORY</p>
            </div>
          </div>
          <div className="col-md-12 col-lg-6">
            <div className="login-signup-box signup-box">
             <h2 className="login-signup-head text-left mt-5 mb-2">Verification Code</h2>
              <p className="login-signup-subhead mb-4">Enter Verification Code</p>
             
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting, setFieldValue, values, errors, touched, validateForm }) => (
                  <Form>
                    <div className="form-group">
                      <label htmlFor="otp">
                        {/* Enter verification code received on your registered phone number and email address. */}
                        {/* Enter Verification Code */}
                      </label>

                      <div className="d-flex otp-box">
                        {['otp1', 'otp2', 'otp3', 'otp4', 'otp5', 'otp6'].map((otp) => (
                          <Field
                            key={otp}
                            type="text"
                            name={otp}
                            maxLength="1"
                            className={`form-control ${errors[otp] && touched[otp] ? 'is-invalid' : ''}`}
                            onChange={(e) => handleInputChange(e, setFieldValue)}
                            onPaste={(e) => handlePaste(e, setFieldValue, validateForm)} // <- yahan validateForm pass karein
                            onKeyDown={(e) => handleKeyDown(e, setFieldValue)}
                          />
                        ))}
                      </div>

                      {[...Array(6)].some((_, i) => touched[`otp${i + 1}`] && errors[`otp${i + 1}`]) && (
                        <div className="text-danger text-center mt-2">All verification fields are required.</div>
                      )}
                    </div>

                    {/* resend-button-active will active after 30 sec. */}
                    {!timerActive && (
                      <p
                        className="mt-3 mb-0 text-right text-14 text-light resend-button-active ml-auto"
                        onClick={handleResend}
                      >
                        Resend Code
                      </p>
                    )}
                    {timerActive && (
                      <p className="mt-3 mb-0 text-right text-14 text-light w-fit">
                        Resend Code ({timer} Sec)
                      </p>
                    )}
                    <button
                      type="submit"
                      className="auth-screen-btn mt-4 mb-2"
                      disabled={isSubmitting}
                    >
                       {isSubmitting ? "Verifying..." : " Verify & Proceed"}
                    </button>
                  </Form>
                )}
              </Formik>
              <Link
                to={getBackPath()}
                className="text-center text-secondary mt-3 mobile-hidden-text f-700 cursor-pointer d-flex align-items-center justify-content-center p-0 w-100 text-decoration-none"
              >
                <svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16.5 7L2.25 7" stroke="#ffffff" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" />
                  <path d="M7.5 1L1.5 7L7.5 13" stroke="#ffffff" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" />
                </svg>
                &nbsp;&nbsp;
                <span className="text-light f-400">Back</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyCode;
